// SegmentBar — dumb, props-only proportional two-segment bar with a counter.
// A fixed-width track holds up to two colour segments (a "good" green group and
// a "bad" red group) sized proportionally to `total`, plus an
// `answered+rejected`/`total` counter. Pure presentational: zero feature/shared
// imports and no i18n — the localized breakdown is passed in via `ariaLabel`.
// Extracted from the request-local CarrierResponsesBar (#1819); styles and
// geometry are preserved verbatim.
import type { CSSProperties } from 'react';

const TRACK_WIDTH_PX = 44;
const MIN_VISIBLE_SEGMENT_PX = 3;

export interface SegmentBarProps {
  total: number;
  answered: number;
  rejected: number;
  /** Caller-owned, already-localized breakdown for the role=img label + title. */
  ariaLabel: string;
  /** Optional data-testid base: emits `${testId}-bar`, `-answered-segment`, `-rejected-segment`. */
  testId?: string;
}

/**
 * Proportional segment widths: raw `44 * group / total`, each non-zero group
 * clamped up to a 3px sliver; overflow (from clamping) shaved off the largest
 * reducible segment, answered first on a tie. A zero-count group is always 0px.
 * Exported for unit testing; not part of the public ui barrel.
 */
export function computeSegmentBarWidths(input: {
  total: number;
  answered: number;
  rejected: number;
}): { answeredPx: number; rejectedPx: number } {
  const { total, answered, rejected } = input;
  if (total === 0) return { answeredPx: 0, rejectedPx: 0 };
  const clamp = (count: number): number => {
    if (count === 0) return 0;
    const raw = (TRACK_WIDTH_PX * count) / total;
    return Math.max(raw, MIN_VISIBLE_SEGMENT_PX);
  };
  let answeredPx = clamp(answered);
  let rejectedPx = clamp(rejected);
  const overflow = answeredPx + rejectedPx - TRACK_WIDTH_PX;
  if (overflow > 0) {
    const answeredReducible = answeredPx > MIN_VISIBLE_SEGMENT_PX;
    const rejectedReducible = rejectedPx > MIN_VISIBLE_SEGMENT_PX;
    if (answeredReducible && (!rejectedReducible || answeredPx >= rejectedPx)) {
      answeredPx = Math.max(answeredPx - overflow, MIN_VISIBLE_SEGMENT_PX);
    } else if (rejectedReducible) {
      rejectedPx = Math.max(rejectedPx - overflow, MIN_VISIBLE_SEGMENT_PX);
    }
  }
  return { answeredPx, rejectedPx };
}

export function SegmentBar({ total, answered, rejected, ariaLabel, testId }: SegmentBarProps) {
  const { answeredPx, rejectedPx } = computeSegmentBarWidths({ total, answered, rejected });
  const trackStyle: CSSProperties = {
    width: TRACK_WIDTH_PX,
    height: 4,
    background: 'var(--bg-3)',
    borderRadius: 2,
    overflow: 'hidden',
    display: 'flex',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <div
        role="img"
        aria-label={ariaLabel}
        title={ariaLabel}
        data-testid={testId ? `${testId}-bar` : undefined}
        style={trackStyle}
      >
        {answered > 0 && (
          <div
            data-testid={testId ? `${testId}-answered-segment` : undefined}
            style={{
              height: '100%',
              flex: '0 0 auto',
              width: `${answeredPx}px`,
              background: 'var(--status-success-fg)',
            }}
          />
        )}
        {rejected > 0 && (
          <div
            data-testid={testId ? `${testId}-rejected-segment` : undefined}
            style={{
              height: '100%',
              flex: '0 0 auto',
              width: `${rejectedPx}px`,
              background: 'var(--status-error-strong)',
            }}
          />
        )}
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>
        {answered + rejected}/{total}
      </span>
    </div>
  );
}
